// React message catalog (architecture §6.11). EN is the source of truth; zh-TW
// (繁體中文) overrides it, and any missing key falls back to EN (then the raw key)
// so the UI never shows a blank. Catalogs are assembled from co-located
// `*.i18n.ts` modules next to each page/component, merged here via import.meta.glob
// — so a screen owns its own strings and adding one needs no central edit.

// Each co-located `*.i18n.ts` module exports `en` and `zhTW` maps (valid JS
// identifiers); `zhTW` feeds the `zh-TW` locale.
type MessageModule = {
  en?: Record<string, string>
  zhTW?: Record<string, string>
}

// Base/shared keys (always present even with no co-located modules).
const baseEn: Record<string, string> = {
  'app.name': 'Geo4Logistics',
  'app.nav.dashboard': 'Dashboard',
  'app.nav.shipments': 'Shipments',
  'app.nav.notifications': 'Notifications',
  'app.nav.settings': 'Settings',
  'app.actions.save': 'Save',
  'app.actions.cancel': 'Cancel',
  'app.actions.sign_out': 'Sign out',
  'app.common.language': 'Language',
  'app.common.theme': 'Theme',
}
const baseZh: Record<string, string> = {
  'app.name': 'Geo4Logistics', // brand — same in every locale (keeps key parity)
  'app.nav.dashboard': '儀表板',
  'app.nav.shipments': '貨件',
  'app.nav.notifications': '通知',
  'app.nav.settings': '設定',
  'app.actions.save': '儲存',
  'app.actions.cancel': '取消',
  'app.actions.sign_out': '登出',
  'app.common.language': '語言',
  'app.common.theme': '佈景主題',
}

const modules = import.meta.glob<MessageModule>('../**/*.i18n.ts', { eager: true })

export const en: Record<string, string> = { ...baseEn }
const zhTW: Record<string, string> = { ...baseZh }
for (const mod of Object.values(modules)) {
  if (mod.en) Object.assign(en, mod.en)
  if (mod.zhTW) Object.assign(zhTW, mod.zhTW)
}

export const catalogs: Record<string, Record<string, string>> = { en, 'zh-TW': zhTW }

// Keys are plain strings (the catalog spans many co-located modules). A missing
// key resolves to EN, then to the key itself.
export type TranslationKey = string
export type TranslationParams = Record<string, string | number>

// Resolve a key for a locale and interpolate `{token}` placeholders.
export function translate(locale: string, key: string, params?: TranslationParams): string {
  let out = catalogs[locale]?.[key] ?? en[key] ?? key
  if (params) {
    for (const [name, value] of Object.entries(params)) {
      out = out.split(`{${name}}`).join(String(value))
    }
  }
  return out
}
